// src/components/events/UpcomingEvents.jsx
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPublicEvents } from "@/store/slices/eventsSlice";
import EventCard from "./EventCard";
import Button from "../common/Button";

const UpcomingEvents = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { events, isLoading } = useSelector((state) => state.events);

  useEffect(() => {
    // Only fetch if we don't already have events
    if (events.length === 0) {
      dispatch(fetchPublicEvents({ sort: "upcoming" }));
    }
  }, [dispatch, events.length]);

  // Show first 4 events
  const displayEvents = events.slice(0, 4);

  return (
    <section className="py-16 bg-white dark:bg-gray-950 transition-colors duration-300">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">
            Upcoming Cycling Events
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Join us for these exciting cycling events across India
          </p>
        </motion.div>

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200/50 dark:border-gray-800 shadow"
              >
                <div className="h-44 bg-gray-200 dark:bg-gray-800" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                  <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Events grid */}
        {!isLoading && displayEvents.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {displayEvents.map((event, index) => (
              <EventCard
                key={event._id}
                event={event}
                index={index}
              />
            ))}
          </div>
        )}

        {/* No events */}
        {!isLoading && displayEvents.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              No upcoming events at the moment. Check back soon!
            </p>
          </div>
        )}

        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-12"
        >
          <Button
            size="lg"
            className="shadow-md hover:shadow-lg"
            onClick={() => navigate("/events")}
          >
            View All Events
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
